import { shortToLong } from '../helpers/icon'
import replaceStorageURLsFromFirebaseToCloudflare from '../helpers/replaceStorageURLsFromFirebaseToCloudflare'
import Attrs from '../models/Attrs'
import type { Flow, FlowPage, InputBox } from '../models/types'
import { generateElementSelectors } from '../styles/selectors'
import renderTemplate from '../helpers/templating'
import { addLanguageController, getBaseBuilderVersion } from '../helpers/utils'

const CONTROLLER = 'components--input-box'

/* NYI - Generate Custom Inline CustomButton */

export default function renderInputBox(
  component: InputBox,
  attrs: Attrs,
  page: FlowPage,
  flow: Flow,
  editingMode: boolean,
  userData?: { [key: string]: unknown }
): string {
  const inputType = getInputType(component, attrs)
  attrs
    .addController(CONTROLLER)
    .addDOMClass('InputBox', inputType, 'input_box_shape')
    .addValue(CONTROLLER, 'input-type', inputType)
    .addAction(
      `${CONTROLLER}:update->flow#update`,
      `${CONTROLLER}:validate->flow#triggerFormValidation`,
      `${CONTROLLER}:actions->flow#dispatchAction`
    )

  if (component.isRequired) attrs.addValue(CONTROLLER, 'required', `${component.isRequired}`)
  if (flow.disable_form_validation) attrs.add('custom-validity', `true`)
  if (component.validation_formula) {
    attrs.addValue(CONTROLLER, 'validation-formula', component.validation_formula)
  } else if (component.input_type === 'phone')
    attrs.addValue(CONTROLLER, 'validation-formula', 'phone_number_us')

  if (component.max_length) attrs.add('maxlength', component.max_length)
  //LUCIAN added following if:
  if (component.input_type === 'number') {
    if (component.range_max) {
      const max = component.range_max.toString()
      attrs.add('max', max)
    }
    if (component.range_min) {
      const min = component.range_min.toString()
      attrs.add('min', min)
    }
    if (component.range_step) {
      const step = component.range_step.toString()
      attrs.add('step', step)
    } else if (component.integer_only) attrs.add('step', '1')
  }
  //LUCIAN end added code

  const label = generateLabel(component, page, flow, attrs, editingMode, userData)
  const input = generateInput(component, page, flow, inputType, attrs, userData)

  return `<div ${attrs.toString()}>${label}${input}</div>`
}

function getInputType(component: InputBox, attrs: Attrs): string {
  const inputType = component && component.input_type
  if (inputType === 'phone') return 'tel'
  if (inputType === 'number' && component.use_tel_input) return 'tel'
  return (
    inputType ||
    (['email_address', 'business_email'].includes(component.validation_formula!) ? 'email' : 'text')
  )
}

function generateLabel(
  component: InputBox,
  page: FlowPage,
  flow: Flow,
  attrs: Attrs,
  editingMode: Boolean,
  userData?: { [key: string]: unknown }
): string {
  if (!component.label) return ``

  const labelWithURLReplacement = replaceStorageURLsFromFirebaseToCloudflare(component.label)
  const labelAfterTemplate =
    replaceStorageURLsFromFirebaseToCloudflare(renderTemplate(labelWithURLReplacement, userData)) ||
    ''

  const builderVersion = getBaseBuilderVersion(flow.builder_version)

  const labelAttrs = new Attrs(attrs.static_render).addTarget(CONTROLLER, 'label').addDOMClass(
    ...generateElementSelectors({
      elementType: 'Label',
      component,
      page,
      builderVersion,
    })
  )

  //* Add template controller if the label has {{
  if (component.label && component.label.includes('{{'))
    labelAttrs
      .addController('template')
      .addValueIfExists('template', 'text', encodeURIComponent(labelWithURLReplacement || ''))
      .addValueIfExists('template', 'always-rerender', component.always_rerender)

  if (component.page_reactive_classes) labelAttrs.addReactiveElement()

  const iconAttrs = new Attrs(attrs.static_render).addDOMClass('icon', 'fas')
  if (component.icon) {
    const icon = typeof component.icon === 'string' ? component.icon.split(' ') : component.icon
    if (icon.length === 2) iconAttrs.addIcon(shortToLong(icon[0]), icon[1])
    else iconAttrs.addIcon('fa', component.icon)
  }
  const labelIcon = component.icon ? `<svg ${iconAttrs}></svg>` : ``
  const requiredSuffix = component.isRequired && flow.required_labels ? ` *` : ''
  //* Add language controller for the label
  addLanguageController({ attrs: labelAttrs, component, keys: ['label'] })

  return `<label ${labelAttrs}>${labelIcon}<span class="label">${labelAfterTemplate}${requiredSuffix}</span></label>`
}

function generateInput(
  component: InputBox,
  page: FlowPage,
  flow: Flow,
  inputType: string,
  attrs: Attrs,
  userData?: { [key: string]: unknown }
): string {
  const builderVersion = getBaseBuilderVersion(flow.builder_version)

  const inputElementName =
    component.multiline && inputType === 'text' ? 'TextareaElement' : 'InputElement'
  const inputClasses = generateElementSelectors({
    elementType: inputElementName,
    component,
    page,
    builderVersion,
  })
  const inputAttrs = new Attrs(attrs.static_render)
    .add('type', inputType)
    .add('name', 'input-' + component.key)
    .add('id', 'input-' + component.id)
    .addDOMClass(inputClasses)
    .addTarget(CONTROLLER, 'input')
    .addParam(CONTROLLER, 'key', component.key)
    .addAction(
      `input->${CONTROLLER}#updateValidStatus`,
      `blur->${CONTROLLER}#triggerFlowValidation`
    )
  if (component.always_disabled) inputAttrs.add('disabled', 'true')
  if (!flow.form_wrapper) inputAttrs.add('form', `flow-${flow.id}`)
  if (component.page_reactive_classes) inputAttrs.addReactiveElement()

  if (component.width) inputAttrs.add('style', `width: ${component.width}ch`)
  if (component.placeholder) {
    const placeholderWithURLReplacement = replaceStorageURLsFromFirebaseToCloudflare(
      component.placeholder
    )
    const placeholderAfterTemplate = (replaceStorageURLsFromFirebaseToCloudflare(
      renderTemplate(placeholderWithURLReplacement, userData)
    ) || '') as string

    inputAttrs.add('placeholder', placeholderAfterTemplate)

    //* Add template controller if the placeholder has {{
    if (component.placeholder && component.placeholder.includes('{{'))
      inputAttrs
        .addController('template')
        .addValueIfExists(
          'template',
          'text',
          encodeURIComponent(placeholderWithURLReplacement || '')
        )
        .addValue('template', 'target', 'placeholder')
  }
  if (component.isRequired) inputAttrs.add('required', `${component.isRequired}`)

  //* Add language controller for the placeholder
  addLanguageController({ attrs: inputAttrs, component, keys: ['placeholder'] })

  switch (component.input_type) {
    // case 'phone': {
    //   /* NYI */
    //   return `PHONE INPUT: PLACEHOLDER`
    // }

    case 'range': {
      const addonAttrs = new Attrs(attrs.static_render)
        .add('type', 'number')
        .addDOMClass('range-addon', ...inputClasses)
        .addTarget(CONTROLLER, 'input')
        .addParam(CONTROLLER, 'key', component.key)
        .addAction(
          `input->${CONTROLLER}#updateValidStatus`,
          `blur->${CONTROLLER}#triggerFlowValidation`
        )
      if (component.placeholder) addonAttrs.add('placeholder', component.placeholder)

      if (component.range_min !== undefined) {
        inputAttrs.add('min', `${component.range_min}`)
        addonAttrs.add('min', `${component.range_min}`)
      }
      if (component.range_max !== undefined) {
        inputAttrs.add('max', `${component.range_max}`)
        addonAttrs.add('max', `${component.range_max}`)
      }
      if (component.range_step !== undefined) {
        inputAttrs.add('step', `${component.range_step}`)
        addonAttrs.add('step', `${component.range_step}`)
      }

      //* Add language controller for the placeholder
      addLanguageController({ attrs: addonAttrs, component, keys: ['placeholder'] })
      return `<div class="slider"><input ${inputAttrs}><input ${addonAttrs}></div>`
    }

    case 'switch': {
      const switchAttrs = new Attrs(attrs.static_render)
        .addDOMClass('ToggleSwitch', inputClasses)
        .addController('switch')
        .addValueIfExists('switch', 'on-label', component.label)
        .addValueIfExists('switch', 'off-label', component.off_label)
        .addValue('switch', 'key', component.key)
        .addAction(`click->switch#toggle`)
        .addAction(`switch:update->flow#update`)
        .addClass('switch', 'on', 'isOn')
        .addAction(`keydown->switch#switchAccessibility`)
        .add('tabindex', '0')

      if (component.isRequired) switchAttrs.add('required', `${component.isRequired}`)

      const switchEl = `<div class="switch"><div class="knob"></div></div>`

      //* Create attrs for off label with language controller
      const offLabelAttrs = new Attrs(attrs.static_render).addDOMClass('label')
      //* Add language controller for the off_label
      addLanguageController({ attrs: offLabelAttrs, component, keys: ['off_label'] })

      const offSwitchLabel =
        component.label && component.label_on_both_side
          ? `<div ${offLabelAttrs}>${component.off_label}</div>`
          : ``

      //* Create attrs for on label with language controller
      const onLabelAttrs = new Attrs(attrs.static_render).addDOMClass('label')
      //* Add language controller for the label
      addLanguageController({ attrs: onLabelAttrs, component, keys: ['label'] })

      const onSwitchLabel =
        component.label && component.label_on_both_side
          ? `<div ${onLabelAttrs}>${component.label}</div>`
          : ``

      //* Create attrs for single label (when not on both sides)
      const switchLabelAttrs = new Attrs(attrs.static_render)
        .addDOMClass('label')
        .addTarget('switch', 'label')
      //* Add language controller for the label
      if (component.label && component.label_on_both_side) {
        addLanguageController({ attrs: switchLabelAttrs, component, keys: ['label'] })
      }

      const label =
        component.label && !component.label_on_both_side ? `<div ${switchLabelAttrs}></div>` : ``

      return `<div ${switchAttrs}>
      ${offSwitchLabel}
      ${switchEl}
      ${onSwitchLabel}
        ${label}
      </div>`
    }

    default: {
      return component.multiline ? `<textarea ${inputAttrs}></textarea>` : `<input ${inputAttrs}>`
    }
  }
}
